import isEqual from 'lodash/isEqual'

import { maLocalStorage } from '@/lib/storage'
import { useQueryParamsValidator } from '@/composables/query-params-validator'
import { SAFE_VALUES } from '@/base/constants/SafeValues'
import { Param, Query } from '@/base/constants/route'

/**
 * Normalize route queries & params based on a list of required queries or a set of default values.
 *
 * @param {object} params - The input route query & params.
 * @param {Array|string} requiredParams - The list of required query & parameters.
 * @param {Array|string} ignoredParams - The list of ignored query & parameters.
 * @returns {object} The normalized route queries & params.
 */
function normalizeRouteParams(params, requiredParams, ignoredParams = []) {
  let normalizedParams = { ...params }

  if (Array.isArray(requiredParams)) {
    normalizedParams = requiredParams.reduce((_acc, _key) => {
      const value = params[_key] || maLocalStorage[_key] || SAFE_VALUES[_key]
      return Object.assign({}, _acc, value ? { [_key]: value } : {})
    }, params || {})
  }

  ignoredParams.forEach((param) => {
    if (Object.hasOwnProperty.call(normalizedParams, param)) {
      delete normalizedParams[param]
    }
  })

  return normalizedParams
}

/**
 * Validates the provided query parameters against a set of predefined validation rules.
 * This function iterates over each parameter and applies validation rules defined in `routeParamsValidator`.
 * If a rule exists for a parameter, it applies the rule to check the validity of its value,
 * otherwise, it retains the original value. The function updates the original allParams object
 * with the validated values and returns a new object with all validated parameters.
 *
 * @param {object} params - The object containing the query parameters to be validated.
 * @param {object} allParams - A comprehensive object of all parameters, which may include more than those to be validated.
 *                             This object is updated in-place with validated values.
 * @returns {object} An object containing the validated query parameters.
 */
function validateParams(params, allParams = {}) {
  const { routeParamsValidator } = useQueryParamsValidator()

  return Object.keys(params).reduce((_acc, _key) => {
    // Applies validation if a rule exists, otherwise retains the original value.
    const value = routeParamsValidator?.[_key] ? routeParamsValidator[_key](params[_key], allParams) : params[_key]
    allParams[_key] = value
    return {
      ..._acc,
      [_key]: value,
    }
  }, {})
}

/**
 * Sort route queries, because some parameters depend on other parameters like store
 *
 * @param {object} routeQuery - query object of the route
 * @returns {object} - sorted query object of the route
 */
function sortQueries(routeQuery) {
  const order = Object.keys(Query)
  return Object.keys(routeQuery)
    .sort(
      (a, b) => order.indexOf(a) - order.indexOf(b),
    )
    .reduce((sortedObject, key) => {
      sortedObject[key] = routeQuery[key]
      return sortedObject
    }, {})
}

/**
 * Normalize route parameters and queries and call the 'next' function if changes are detected.
 *
 * @param {object} to - The destination route object.
 * @param {object} from - The current route object.
 * @param {Function} next - The 'next' function used for route navigation.
 * @return {Function<next | void>} - Returns a function that removes the registered guard.
 */
function maParamsQueryNormalizeGuard(to, from, next) {
  to.query = sortQueries(to.query)
  const params = validateParams(normalizeRouteParams(to.params, to.meta?.requiredParams, to.meta?.ignoredParams), { ...to.query, ...to.params })
  const query = validateParams(normalizeRouteParams(to.query, to.meta?.requiredQueries, to.meta?.ignoredQueries), { ...to.query, ...params })

  /*
     * Sometimes during navigation transitions, the active app information is retrieved from local storage,
     * which may not match the main app itself or its competitors.
     * In such cases, the initial active app should be the main app itself.
     */
  if (to.meta?.requiredQueries?.includes(Query.ACTIVE_APP) && !to.query[Query.ACTIVE_APP]) {
    query[Query.ACTIVE_APP] = params[Param.TRACK_ID]
  }

  if (!isEqual(params, to.params) || !isEqual(query, to.query)) {
    to.params = params
    to.query = query
    return next(to)
  }

  next()
}

export {
  maParamsQueryNormalizeGuard,
}
